import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePatientHealthIdentifiers } from "features/patients/hooks/usePatientHealthIdentifiers";
import { sendToExtension } from "features/pcrsExtension";

// CDM patient details come from the PCRS cdm-ws service via the extension.
// The extension handles the user-admin login + token internally, so the
// webapp only supplies the endpoints, the PCRS credentials and the card.
// The credentials (username + password) are entered by the user and kept in
// the encrypted browser store — see useStoredPcrsCredentials.
const CDM_ENDPOINT = process.env.REACT_APP_SSPCRS_CDM_ENDPOINT;
const USER_ADMIN_ENDPOINT = process.env.REACT_APP_SSPCRS_USER_ADMIN_ENDPOINT;

// PCRS wants the treatment date as dd/MM/yyyy.
const formatTreatmentDate = (date) => format(date, "dd/MM/yyyy");

// Collect the textContent of every descendant matching localName, regardless
// of namespace prefix (PCRS returns ns2:-prefixed elements).
const collectByLocalName = (element, localName) =>
  Array.from(element.getElementsByTagNameNS("*", localName)).map((node) =>
    node.textContent.trim()
  );

// getPatientDetailsResponse is flat apart from the repeating allPatientGmsNumbers
// (a wrapper of <patientGmsNumber> elements) and errors — collect those into
// arrays, everything else as a value. The response is namespace-prefixed, so
// match elements by local name, not qualified name.
const parsePatientDetails = (soapXml) => {
  const document = new DOMParser().parseFromString(soapXml, "application/xml");
  const response = document.getElementsByTagNameNS(
    "*",
    "getPatientDetailsResponse"
  )[0];
  if (!response) return null;

  const fields = {
    allPatientGmsNumbers: collectByLocalName(response, "patientGmsNumber"),
    errors: collectByLocalName(response, "errors"),
  };

  for (const child of response.children) {
    const name = child.localName;

    // The two repeating/wrapper elements are handled above.
    if (name === "allPatientGmsNumbers" || name === "errors") continue;

    fields[name] = child.textContent.trim();
  }

  return fields;
};

const useCdmPatientDetailsReal = ({
  patientId,
  cardType = "GMS",
  treatmentDate,
  username,
  password,
  // Bumped by the caller when credentials are (re)saved, so the query refetches
  // with the new values instead of a stale closure.
  credentialsVersion = 0,
  enabled = true,
} = {}) => {
  const { data: patient } = usePatientData(patientId);
  const { data: healthIdentifiers, isLoading: isHealthIdentifiersLoading } =
    usePatientHealthIdentifiers(patientId);

  // When the patient has an HAA number, use it as-is with the "HAA" card type
  // instead of the GMS/medical card number.
  const haaNumber = healthIdentifiers?.haaNumber;
  const cardNumber = haaNumber || patient?.medicalCardNumber;
  const resolvedCardType = haaNumber ? "HAA" : cardType;

  const resolvedTreatmentDate = treatmentDate ?? formatTreatmentDate(new Date());

  const isEnabled =
    enabled &&
    !isHealthIdentifiersLoading &&
    !!cardNumber &&
    !!username &&
    !!password;

  return useQuery({
    queryKey: [
      "cdmPatientDetails",
      patientId,
      cardNumber,
      resolvedCardType,
      resolvedTreatmentDate,
      credentialsVersion,
    ],
    queryFn: async () => {
      const message = {
        operation: "getPatientDetails",
        endpoints: { cdm: CDM_ENDPOINT, userAdmin: USER_ADMIN_ENDPOINT },
        credentials: { username, password },
        params: {
          cardNumber,
          cardType: resolvedCardType,
          treatmentDate: resolvedTreatmentDate,
        },
      };

      let response;
      try {
        response = await sendToExtension(message);
      } catch (error) {
        console.error("CDM patient details — extension not reachable:", error);

        throw error instanceof Error ? error : new Error(String(error));
      }

      if (!response?.ok) {
        console.error(
          "CDM patient details — connection error:",
          response?.error,
          response?.status
        );

        throw new Error(
          response?.error ??
            `PCRS connection error (status ${response?.status ?? "unknown"})`
        );
      }

      // Retain the raw SOAP body alongside the parsed fields so it can be
      // posted to the BE for authoritative persistence/audit (it re-parses
      // rather than trusting the FE parse). parsePatientDetails can return null
      // for an unexpected shape — still surface the raw XML in that case.
      const parsed = parsePatientDetails(response.body);

      return { ...(parsed ?? {}), rawXml: response.body ?? null };
    },
    enabled: isEnabled,
    // A connectivity failure is surfaced to the page (which re-prompts for
    // credentials) rather than retried or shown as a generic global toast.
    retry: false,
    meta: { skipGlobalError: true },
  });
};

// TEMP: CDM patient details mocked — benign data so the CDM screen treats the
// web service as connected, without any extension call. Flip MOCK_PCRS to false
// to restore the real hook (implementation preserved above).
const MOCK_PCRS = process.env.REACT_APP_CDM_MOCK_PCRS === "true";

export const useCdmPatientDetails = MOCK_PCRS
  ? () => ({
      data: {
        registrationDate: "2020-01-20",
        gpGmsNumber: "",
        errors: [],
        rawXml: null,
      },
      isError: false,
      isFetching: false,
      refetch: async () => {},
    })
  : useCdmPatientDetailsReal;
